/**
 * Liga e desliga cada inferência local do Messenger, uma por uma.
 *
 * Motivo de existir: os modelos são carregados na memória da aba e em
 * aparelho modesto isso estoura. Em vez de arrancar o código, cada recurso
 * ganhou uma chave — dá pra deixar só o que cabe.
 *
 * O corte é feito nos `*Supported()` de cada módulo, que o app já consulta
 * antes de oferecer o recurso. Então desligar aqui degrada com elegância,
 * pelo mesmo caminho de um browser sem WebAssembly. As funções de inferência
 * também falham cedo, como segunda linha de defesa para quem não checou.
 *
 * Padrão atual: tudo desligado, até resolvermos o consumo de memória.
 */

export type RecursoId =
  | "transcricao"
  | "busca"
  | "documentos"
  | "transcricaoGoogle"
  | "idade"
  | "emocao"
  | "genero"
  | "nomes"
  | "imagemTexto"
  | "sentimento"
  | "legenda"
  | "objetos"
  | "resumo"
  | "assunto"
  | "traducao";

export interface Recurso {
  id: RecursoId;
  nome: string;
  descricao: string;
  /** O que deixa de funcionar com ele desligado. */
  custo: string;
  modelo: string;
  /** Tamanho aproximado baixado e mantido em memória. */
  peso: string;
  /** True quando o processamento sai do aparelho. Muda o aviso na tela. */
  remoto?: boolean;
  /** Tool do wuweiAgent que esta chave governa.
   *
   *  É o que separa uma chave de um enfeite: `bloqueioDaTool` recusa a tool
   *  antes de o modelo ser baixado, então quem escrever o próximo recurso não
   *  precisa lembrar de perguntar — a chave já vale. */
  tool?: string;
  /** Heap WASM que ESTE modelo sozinho ocupou, em MB, medido no aparelho em
   *  6 e 7/ago/2026 pelo banco de provas.
   *
   *  `peso` é o texto que a pessoa lê; este é o número com que se decide se
   *  cabe. Fica separado de propósito: comparar string não dá para fazer. */
  heapMb?: number;
}

/** Quanto o WebView do aparelho de referência entrega à aba INTEIRA — interface,
 *  banco local e cache de imagens no mesmo bolso. Medido em 6/ago/2026.
 *
 *  O modelo que pede mais que isto não fica lento: o Android mata o app, sem
 *  exceção e sem aviso. Por isso o número vive aqui e não num comentário. */
export const ORCAMENTO_MB = 1024;

/** Daqui para cima o modelo sozinho já leva a maior parte do orçamento e não
 *  sobra espaço para um segundo. */
const APERTA_MB = 700;

/** Daqui para cima é aposta: sobra pouco mais que a interface. */
const BEIRA_MB = 900;

export const RECURSOS: Recurso[] = [
  {
    id: "transcricao",
    nome: "Transcrição de áudio",
    descricao: "Escreve o texto do que foi falado nos áudios, no próprio aparelho.",
    custo: "Áudio continua gravando, enviando e tocando — só não vira texto.",
    modelo: "Xenova/whisper-base",
    peso: "~150 MB",
    tool: "transcribe",
  },
  {
    id: "busca",
    nome: "Busca semântica",
    descricao: "Acha mensagem por sentido, não só por palavra igual.",
    custo: "A busca por palavra exata continua funcionando.",
    modelo: "Xenova/multilingual-e5-small",
    peso: "~118 MB",
    tool: "text-embeddings",
  },
  {
    id: "transcricaoGoogle",
    nome: "Transcrição pelo Google",
    descricao:
      "Plano B quando a transcrição local está desligada: usa o reconhecimento de voz do Chrome.",
    custo: "Sem ele e sem a transcrição local, áudio nenhum vira texto.",
    modelo: "Web Speech API (Chrome)",
    peso: "não ocupa memória",
    remoto: true,
  },
  {
    id: "documentos",
    nome: "Leitura de documentos",
    descricao: "Extrai o texto de PDF recebido para ele entrar na busca.",
    custo: "O PDF continua chegando e abrindo — só não entra na busca.",
    modelo: "pdf.js (sem modelo de IA)",
    peso: "leve",
    tool: "extract",
  },

  // Daqui para baixo: o que até 7/ago/2026 só existia como linha do banco de
  // provas, medido e nunca ligável. Medir e não deixar usar é meia obra — quem
  // pagou o download merece o recurso.
  //
  // A ordem é a mesma do banco de provas, do mais barato ao mais caro. Quem
  // vai ligando de cima para baixo encontra o limite do aparelho antes de
  // bater nele; quem começasse pelo fim descobriria o limite com o app
  // fechando.
  {
    id: "idade",
    nome: "Estimar idade em foto",
    descricao:
      "Olha o rosto da foto e chuta a faixa de idade. Precisa achar o rosto antes, então carrega o detector junto.",
    custo: "A foto continua chegando e abrindo igual — ninguém chuta idade de ninguém.",
    modelo: "jdp8/vit-age-classifier",
    peso: "~229 MB",
    tool: "age-estimation",
    heapMb: 229,
  },
  {
    id: "emocao",
    nome: "Emoção no rosto",
    descricao:
      "Diz se o rosto da foto parece alegre, triste, com raiva. Também precisa achar o rosto antes.",
    custo: "A foto continua igual; o humor de quem está nela fica por sua conta.",
    modelo: "Xenova/facial_emotions_image_detection",
    peso: "~229 MB",
    tool: "facial-emotion",
    heapMb: 229,
  },
  {
    id: "genero",
    nome: "Gênero percebido no rosto",
    descricao:
      "Chuta o gênero que o rosto aparenta. É palpite sobre aparência, não sobre quem a pessoa é.",
    custo: "A foto continua igual e nada é chutado sobre quem aparece nela.",
    modelo: "onnx-community/gender-classification-ONNX",
    peso: "~229 MB",
    tool: "perceived-gender",
    heapMb: 229,
  },
  {
    id: "nomes",
    nome: "Nomes e lugares",
    descricao: "Marca pessoas, lugares e empresas citados no texto das mensagens.",
    custo:
      "A busca por palavra continua achando o nome escrito — só não dá para pedir “as mensagens que citam alguém”.",
    modelo: "Xenova/bert-base-NER",
    peso: "~280 MB",
    tool: "ner",
    heapMb: 280,
  },
  {
    id: "imagemTexto",
    nome: "Achar foto pela descrição",
    descricao:
      "Põe foto e texto na mesma régua: dá para procurar “praia” e achar a foto da praia, sem ninguém ter escrito “praia”.",
    custo: "As fotos continuam na conversa; achar é rolando até elas.",
    modelo: "Xenova/clip-vit-base-patch32",
    peso: "~386 MB",
    tool: "zero-shot-image",
    heapMb: 386,
  },
  {
    id: "sentimento",
    nome: "Tom da mensagem",
    descricao: "Diz se o texto soa positivo, negativo ou neutro.",
    custo: "Você lê a mensagem e julga o tom sozinho, como sempre fez.",
    modelo: "Xenova/distilbert-base-multilingual-cased-sentiments-student",
    peso: "~391 MB",
    tool: "sentiment",
    heapMb: 391,
  },
  {
    id: "legenda",
    nome: "Descrever a foto em palavras",
    descricao: "Escreve uma frase curta sobre o que aparece na imagem.",
    custo:
      "A foto chega sem descrição escrita — quem não enxerga a imagem fica sem saber o que tem nela.",
    modelo: "Xenova/vit-gpt2-image-captioning",
    peso: "~523 MB",
    tool: "caption",
    heapMb: 523,
  },
  {
    id: "objetos",
    nome: "Reconhecer objetos na foto",
    descricao: "Aponta o que aparece na imagem e onde: pessoa, carro, cachorro.",
    custo: "A foto abre igual; só não dá para procurar “as que têm cachorro”.",
    modelo: "Xenova/detr-resnet-50",
    peso: "~544 MB",
    tool: "detect-objects",
    heapMb: 544,
  },
  {
    id: "resumo",
    nome: "Resumir texto longo",
    descricao: "Encolhe mensagem ou documento comprido em poucas linhas.",
    custo: "O texto longo continua chegando inteiro — para saber o que diz, é ler tudo.",
    modelo: "Xenova/distilbart-cnn-6-6",
    peso: "~724 MB",
    tool: "summarize",
    heapMb: 724,
  },
  {
    id: "assunto",
    nome: "Separar por assunto",
    descricao:
      "Põe a mensagem nas categorias que você mesmo escrever, sem treinar nada antes.",
    custo: "As mensagens ficam todas no mesmo monte; separar é no olho.",
    modelo: "Xenova/mDeBERTa-v3-base-xnli-multilingual-nli-2mil7",
    peso: "~922 MB",
    tool: "zero-shot-text",
    heapMb: 922,
  },
  {
    id: "traducao",
    nome: "Traduzir mensagem",
    descricao: "Passaria a mensagem de uma língua para outra dentro do aparelho.",
    custo: "Mensagem em outra língua chega como veio.",
    modelo: "Xenova/nllb-200-distilled-600M",
    peso: "~2796 MB",
    tool: "translate",
    // Medido em 7/ago/2026: quase três vezes o orçamento inteiro do app. Fica
    // na lista, e não escondido, porque a pergunta "cadê traduzir?" volta
    // sempre — melhor responder com o número do que com o silêncio.
    heapMb: 2796,
  },
];

/* ── Veredito por aparelho ──────────────────────────────────────────────────
 *
 * `heapMb` diz o que o modelo custou onde foi medido; o veredito diz o que ele
 * fez NESTE aparelho. São coisas diferentes e a segunda é a que decide: o mesmo
 * modelo de 523 MB cabe num aparelho e fecha o app noutro, e a única forma de
 * saber qual é o caso é ter tentado aqui.
 *
 * Fica gravado porque a medição é cara — baixa centenas de MB e pressiona a
 * memória do app. Refazer a cada abertura seria cobrar de novo por uma resposta
 * que já se tem.
 */

export type EstadoVeredito = "cabe" | "naocabe" | "naotestado";

export interface VereditoRecurso {
  estado: EstadoVeredito;
  /** Em português e com os números dentro. Um veredito que não pode ser
   *  conferido por quem discorda dele é só uma ordem. */
  motivo: string;
  /** Heap WASM que o modelo ocupou aqui, quando chegou a rodar. */
  custoMb?: number;
  quando: string;
}

export interface VereditoAparelho {
  /** A que aparelho estes números pertencem. Ver `assinaturaAparelho`. */
  aparelho: string;
  /** Teto medido na hora da bateria — é dele que sai a margem de segurança. */
  tetoMb: number | null;
  quando: string;
  itens: Partial<Record<RecursoId, VereditoRecurso>>;
}

const CHAVE_VEREDITO = "wuwei.veredito.v1";

/** Identidade grosseira do aparelho.
 *
 *  O localStorage já é por origem e por navegador, mas isso não basta: o mesmo
 *  perfil pode ser restaurado noutro celular, e aí o veredito medido no antigo
 *  passaria a governar o novo — liberando num aparelho fraco o que só coubera
 *  no forte. Guardar de quem é o número permite descartá-lo quando o dono muda.
 *
 *  Não é impressão digital para rastrear ninguém: é userAgent e contagem de
 *  núcleos, que a página já lê, e fica só no aparelho. */
export function assinaturaAparelho(): string {
  const n = navigator as unknown as { deviceMemory?: number; hardwareConcurrency?: number };
  return [navigator.userAgent, n.hardwareConcurrency ?? "?", n.deviceMemory ?? "?"].join("|");
}

/** O que foi medido AQUI. Null quando nunca se mediu ou quando o registro é de
 *  outro aparelho — e "de outro aparelho" vale como "não medido", nunca como
 *  permissão herdada. */
export function lerVereditos(): VereditoAparelho | null {
  try {
    const cru = localStorage.getItem(CHAVE_VEREDITO);
    if (!cru) return null;
    const v = JSON.parse(cru) as VereditoAparelho;
    if (!v || typeof v !== "object" || !v.itens) return null;
    if (v.aparelho !== assinaturaAparelho()) return null;
    return v;
  } catch {
    return null;
  }
}

export function limparVereditos(): void {
  try {
    localStorage.removeItem(CHAVE_VEREDITO);
  } catch {
    // sem persistência: nada a limpar
  }
}

export function vereditoDe(id: RecursoId): VereditoRecurso | null {
  return lerVereditos()?.itens[id] ?? null;
}

/** Grava um veredito, preservando os dos outros recursos.
 *
 *  Escreve um por vez, e não a bateria inteira no fim, porque o fim pode não
 *  chegar: se o modelo seguinte fechar o app, o que já foi medido tem de ter
 *  sobrevivido. */
export function marcarVeredito(
  id: RecursoId,
  v: Omit<VereditoRecurso, "quando"> & { quando?: string },
  tetoMb?: number | null,
): void {
  try {
    const atual = lerVereditos();
    const novo: VereditoAparelho = atual ?? {
      aparelho: assinaturaAparelho(),
      tetoMb: tetoMb ?? null,
      quando: new Date().toISOString(),
      itens: {},
    };
    if (tetoMb !== undefined) novo.tetoMb = tetoMb;
    novo.quando = new Date().toISOString();
    novo.itens[id] = { ...v, quando: v.quando ?? novo.quando };
    localStorage.setItem(CHAVE_VEREDITO, JSON.stringify(novo));
  } catch {
    // sem persistência: o veredito vale só para esta sessão
  }
  // Recurso que não cabe não pode ficar com a chave ligada de antes. O bloqueio
  // em `indisponivel` já o recusaria, mas deixar "ligado" gravado é guardar uma
  // afirmação falsa esperando que alguém tropece nela.
  if (v.estado === "naocabe") setRecurso(id, false);
}

const PREFIXO = "wuwei.inferencia.";

/** Desligado até resolvermos o consumo de memória. */
const PADRAO = false;

export function recursoAtivo(id: RecursoId): boolean {
  try {
    const v = localStorage.getItem(PREFIXO + id);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    // localStorage pode não existir (modo restrito); cai no padrão
  }
  return PADRAO;
}

export function setRecurso(id: RecursoId, ativo: boolean): void {
  try {
    localStorage.setItem(PREFIXO + id, ativo ? "1" : "0");
  } catch {
    // sem persistência disponível: nada a fazer
  }
}

/**
 * Se o aparelho consegue rodar o recurso, independente da chave.
 *
 * Existe porque ligar a chave não basta: a transcrição do Google depende de
 * uma API que o WebView do Android não expõe — só o Chrome completo tem. Sem
 * isto, ligar e nada acontecer parecia defeito.
 *
 * Retorna null quando disponível, ou o motivo de não estar.
 */
export function indisponivel(id: RecursoId): string | null {
  if (id === "transcricaoGoogle") {
    const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
    if (!w.SpeechRecognition && !w.webkitSpeechRecognition) {
      return "não existe neste app — só no Chrome pelo navegador";
    }
    return null;
  }
  if (typeof WebAssembly === "undefined") return "este navegador não tem WebAssembly";
  if (id === "transcricao" && typeof OfflineAudioContext === "undefined") {
    return "sem suporte a áudio";
  }
  const r = RECURSOS.find((x) => x.id === id);
  // Modelo que pede mais memória do que o app inteiro tem não é "o lento": é o
  // que fecha o app na cara de quem ligou. Oferecer a chave e deixar o usuário
  // descobrir sozinho seria mentir por omissão — aqui ele já nasce recusado, e
  // com o número na mão para conferir a recusa.
  if (r?.heapMb != null && r.heapMb > ORCAMENTO_MB) {
    return `não cabe — precisa de ~${r.heapMb} MB e este app tem ~${ORCAMENTO_MB} MB; ligar fecharia o app`;
  }
  // O que a bateria reprovou NESTE aparelho vale mais que o número de
  // referência: `heapMb` foi medido noutro lugar, o veredito foi medido aqui.
  const v = vereditoDe(id);
  if (v?.estado === "naocabe") return `não cabe neste aparelho — ${v.motivo}`;
  return null;
}

/** Aviso de aperto para o que cabe, mas mal.
 *
 *  Entre "cabe" e "não cabe" existe uma faixa em que o modelo entra sozinho e
 *  não sobrevive a companhia. Sem dizer isso, ligar dois pesados parece um
 *  direito — e o app fecha sem explicar por quê. Devolve null quando não há
 *  nada a avisar. */
export function avisoDeMemoria(id: RecursoId): string | null {
  const r = RECURSOS.find((x) => x.id === id);
  const mb = r?.heapMb;
  if (mb == null || mb > ORCAMENTO_MB) return null;
  if (mb >= BEIRA_MB) {
    return `Beira o limite: sozinho ocupa ~${mb} MB dos ~${ORCAMENTO_MB} MB do app. Pode fechar o app — ligue só na hora de usar.`;
  }
  if (mb >= APERTA_MB) {
    return `Pesado: ~${mb} MB dos ~${ORCAMENTO_MB} MB do app. Não segura outro modelo ligado junto.`;
  }
  return null;
}

/**
 * Motivo para NÃO deixar uma tool do wuweiAgent rodar agora, ou null quando pode.
 *
 * Existe para a chave valer no lugar onde o modelo é baixado, e não só na tela
 * de configuração. O caminho antigo era cada cliente (whisper, embeddings,
 * extração) perguntar por conta própria antes de chamar o pool; funcionou
 * enquanto eram três, e cada recurso novo virava mais um lugar onde dá para
 * esquecer de perguntar.
 *
 * Tool que não está na lista de recursos não é governada por chave nenhuma e
 * passa direto: recusar por omissão quebraria o que já funciona hoje.
 */
export function bloqueioDaTool(tool: string): string | null {
  const r = RECURSOS.find((x) => x.tool === tool);
  if (!r) return null;
  if (!recursoAtivo(r.id)) return motivoDesligado(r.id);
  const motivo = indisponivel(r.id);
  // Chave ligada num recurso indisponível não vale: é justamente o caso do
  // modelo que não cabe, onde rodar significa fechar o app.
  if (motivo) return `${motivoDesligado(r.id)} (${motivo})`;
  return null;
}

/**
 * Testa de verdade se o reconhecimento de fala responde neste aparelho.
 *
 * Existe porque a checagem "o objeto existe?" mente: medi sob Playwright que o
 * Chromium expõe `webkitSpeechRecognition` e **nenhum evento dispara** — nem
 * `start`. Um motor inerte passa por disponível. O único jeito honesto de
 * saber é mandar iniciar e ver se ele responde.
 *
 * Não consome áudio nem manda nada para fora: assim que confirma o `start`,
 * aborta.
 */
export function testarFala(): Promise<{ ok: boolean; motivo: string }> {
  return new Promise((resolve) => {
    const w = window as unknown as {
      SpeechRecognition?: new () => SRLike;
      webkitSpeechRecognition?: new () => SRLike;
    };
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) {
      resolve({ ok: false, motivo: "não existe neste app — só no Chrome pelo navegador" });
      return;
    }
    let pronto = false;
    const sr = new SR();
    const encerra = (ok: boolean, motivo: string) => {
      if (pronto) return;
      pronto = true;
      try {
        sr.abort();
      } catch {
        // já estava parado
      }
      resolve({ ok, motivo });
    };
    sr.onstart = () => encerra(true, "responde normalmente");
    sr.onerror = (ev: unknown) => {
      const c = (ev as { error?: string } | null)?.error || "desconhecido";
      // `no-speech` é resposta boa: o motor está vivo, só não ouviu nada.
      if (c === "no-speech") encerra(true, "responde normalmente");
      else encerra(false, c);
    };
    // Sem resposta em 4s = motor inerte, o caso que a checagem antiga não via.
    setTimeout(() => encerra(false, "não respondeu — o motor existe mas está inerte"), 4000);
    try {
      sr.start();
    } catch (e) {
      encerra(false, String((e as Error)?.message || e).slice(0, 60));
    }
  });
}

interface SRLike {
  onstart: (() => void) | null;
  onerror: ((ev: unknown) => void) | null;
  start: () => void;
  abort: () => void;
}

/** True se qualquer recurso está ligado — usado pelo pool de workers. */
export function algumRecursoAtivo(): boolean {
  return RECURSOS.some((r) => recursoAtivo(r.id));
}

/** Mensagem única para quem tentar inferir com o recurso desligado. */
export function motivoDesligado(id: RecursoId): string {
  return `inferência local desligada: ${id}`;
}

/**
 * Ponte para os testes, no mesmo padrão de `window.__wuweiCripto`.
 *
 * A decisão de o que pode rodar é uma função pura; perguntar direto a ela vale
 * mais do que deduzi-la pela tela, porque a tela pode estar certa por acaso —
 * um item cinza parece recusado mesmo quando a recusa não chega ao pool.
 */
declare global {
  interface Window {
    __wuweiInferencia: {
      RECURSOS: typeof RECURSOS;
      ORCAMENTO_MB: number;
      recursoAtivo: typeof recursoAtivo;
      setRecurso: typeof setRecurso;
      indisponivel: typeof indisponivel;
      avisoDeMemoria: typeof avisoDeMemoria;
      bloqueioDaTool: typeof bloqueioDaTool;
      lerVereditos: typeof lerVereditos;
      vereditoDe: typeof vereditoDe;
      marcarVeredito: typeof marcarVeredito;
      limparVereditos: typeof limparVereditos;
      assinaturaAparelho: typeof assinaturaAparelho;
    };
  }
}

if (typeof window !== "undefined") {
  window.__wuweiInferencia = {
    RECURSOS,
    ORCAMENTO_MB,
    recursoAtivo,
    setRecurso,
    indisponivel,
    avisoDeMemoria,
    bloqueioDaTool,
    lerVereditos,
    vereditoDe,
    marcarVeredito,
    limparVereditos,
    assinaturaAparelho,
  };
}
